import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, forkJoin, take, tap } from 'rxjs';
import { CheckoutMode, ICartItem, InvoiceStatus, InvoicingService, ISaleStatus, ItemType } from '../../../core/services/invoicing.service';
import { saleLinesHaveNonZeroUnitPrice } from '../../../core/services/invoicing-cart.util';
import { MerchantsService } from '../../../core/services/merchants.service';
import { StoresService } from '../../../core/services/stores.service';
import { UserService } from '../../../core/services/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TextInputComponent } from '../../../shared-with-client/text-input/text-input.component';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { JsonPipe, NgTemplateOutlet } from '@angular/common';
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { TabBarComponent } from '../../../shared/tab-bar/tab-bar.component';
import { ClientSelectorComponent } from '../../../shared/client-selector/client-selector.component';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';

@Component({
    selector: 'app-invoice-add-items',
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        TextInputComponent,
        LoaderComponent,
        NgTemplateOutlet,
        MerchantCurrencyPipe,
        TabBarComponent,
        ClientSelectorComponent,
        ButtonComponent,
    ],
    templateUrl: './invoice-add-items.component.html',
    styleUrl: './invoice-add-items.component.scss',
})
export class InvoiceAddItemsComponent implements OnInit {
    private storesService = inject(StoresService);
    private invoicingService = inject(InvoicingService);
    private merchantService = inject(MerchantsService);
    private userService = inject(UserService);
    private router = inject(Router);
    private translateService = inject(TranslateService);
    private fb = inject(FormBuilder);
    private activatedRoute = inject(ActivatedRoute);

    public currentMerchant = toSignal(this.merchantService.activeMerchant, { initialValue: null });
    public currentStore = toSignal(this.storesService.getActiveStore());

    public invoicingCart = toSignal(this.invoicingService.invoicingCart$);
    public selectedPatient = toSignal(this.invoicingService.invoicingPatient$, { initialValue: null });

    public checkoutMode = toSignal(this.invoicingService.checkoutMode$, { initialValue: null });

    public activeTab = signal<'services' | 'products'>('services');
    public addPatientOpen = signal<boolean>(false);
    public loading = signal<boolean>(true);

    public availableServices = signal<any[]>([]);
    public filteredServices = signal<any[]>([]);

    public availableProducts = signal<any[]>([]);
    public filteredProducts = signal<any[]>([]);

    public availableProviders = signal<any[]>([]);

    public separatorsWithServices = signal<any[]>(null);

    public filterForm = this.fb.group({
        search: [''],
    });

    public tabOptions = [
        { label: this.translateService.instant('INVOICE_ADD_ITEMS.services'), value: 'services' },
        { label: this.translateService.instant('INVOICE_ADD_ITEMS.products'), value: 'products' },
    ];

    public ItemType = ItemType;
    public InvoiceStatus = InvoiceStatus;
    CheckoutMode = CheckoutMode;
    public saving = signal<boolean>(false);

    public isInvoicingActive = toSignal(this.storesService.isInvoicingActive());

    public loadingTotals = toSignal(this.invoicingService.loadingTotals$, { initialValue: false });

    public hasNewItemsInCart = computed(() => {
        const cart = this.invoicingCart();
        if (!cart) {
            return false;
        }
        return cart.saleLines.some((line) => !line.readonly);
    });

    public allItemsHaveZeroUnitPrice = computed(() => {
        const saleLines = this.invoicingCart()?.saleLines;
        return !!saleLines?.length && !saleLinesHaveNonZeroUnitPrice(saleLines);
    });

    public canContinue = computed(() => {
        return !!this.invoicingCart()?.saleLines?.length && !this.loadingTotals() && !this.allItemsHaveZeroUnitPrice();
    });

    ngOnInit(): void {
        this.storesService.getStoreProviders(this.currentStore().id).subscribe((providers) => {
            this.availableProviders.set(providers);
            this.invoicingService.$availableProvidersForCommissions.next(providers);
            this.initialize();
        });
    }

    canShowClientContacts(): boolean {
        return this.userService.canShowClientContacts(this.currentStore());
    }

    initialize() {
        const intialRequests: { [key: string]: any } = {
            services: this.fetchServices(),
            products: this.fetchProducts(),
        };

        const invoiceCreationMode = this.activatedRoute.snapshot?.data['invoiceCreationMode'] as CheckoutMode;
        this.invoicingService.checkoutMode$.next(invoiceCreationMode);

        const saleId = this.activatedRoute.snapshot?.params['saleId'];
        if (saleId) {
            // fetch sale details to populate cart and patient info if saleId is present in route
            intialRequests.sale = this.fetchSaleDetails(saleId);
        }

        forkJoin(intialRequests).subscribe(() => {
            this.loading.set(false);
        });

        this.invoicingService.editablePreview$.next(true);

        this.filterForm
            .get('search')
            ?.valueChanges.pipe(debounceTime(300))
            .subscribe((searchValue) => {
                const searchLower = searchValue?.toLowerCase() || '';

                if (this.activeTab() === 'services') {
                    this.filterServices(searchLower);
                } else if (this.activeTab() === 'products') {
                    this.filterProducts(searchLower);
                }
            });
    }

    ngOnDestroy(): void {
        this.invoicingService.editablePreview$.next(false);
    }

    filterServices(searchLower: string) {
        this.separatorsWithServices.set(this.allSeparatorsWithServices());

        if (!searchLower) {
            this.filteredServices.set(this.availableServices());
            return;
        }

        const categoriesWithServices = [];

        const filteredCategories = this.availableServices().filter((service) => {
            const isSeparator = service.category;
            const matchesSearch = service.name.toLowerCase().includes(searchLower);

            if (matchesSearch) {
                if (service.categoryName) {
                    categoriesWithServices.push(service.categoryName);
                }
                return true; // Keep services that match the search
            }

            if (isSeparator) {
                return true; // Keep category separators
            }

            return false;
        });

        this.separatorsWithServices.set(categoriesWithServices);

        this.filteredServices.set(filteredCategories);
    }

    allSeparatorsWithServices() {
        const categoriesWithServices = [];

        this.availableServices().forEach((service) => {
            if (service.categoryName) {
                categoriesWithServices.push(service.categoryName);
            }
        });
        return categoriesWithServices;
    }

    filterProducts(searchLower: string) {
        if (!searchLower) {
            this.filteredProducts.set(this.availableProducts());
            return;
        }

        const filteredProducts = this.availableProducts().filter((product) => product.name.toLowerCase().includes(searchLower));

        this.filteredProducts.set(filteredProducts);
    }

    onTabChanged(tabValue: string) {
        this.loading.set(false);
        this.filterForm.reset();
        if (tabValue === 'products' && this.availableProducts().length === 0) {
            this.fetchProducts();
        } else if (tabValue === 'services' && this.availableServices().length === 0) {
            this.fetchServices();
        }
    }

    fetchServices() {
        this.loading.set(true);
        return this.storesService.getStoreServices(this.currentStore().id).pipe(
            tap((res) => {
                this.availableServices.set(res);
                this.filteredServices.set(res);
                this.separatorsWithServices.set(this.allSeparatorsWithServices());
                this.loading.set(false);
            }),
        );
    }

    fetchSaleDetails(saleId: number) {
        this.loading.set(true);

        return this.invoicingService.getSaleDetails(saleId, this.currentStore().id).pipe(
            tap({
                next: (res) => {
                    // if invoicing remaing and sale is fully invoiced, or editing a non-draft sale with invoicing active, redirect to sales page
                    if (
                        (this.checkoutMode() === CheckoutMode.INVOICE_REMAINING && res.invoiceStatus === InvoiceStatus.INVOICED) ||
                        (this.checkoutMode() === CheckoutMode.EDIT_SALE && this.isInvoicingActive() && res.status !== ISaleStatus.DRAFT)
                    ) {
                        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/sales');
                        return;
                    }

                    if (res.merchantClient || res.clientVatNumber || res.clientName) {
                        this.invoicingService.setPatientFromSale(res);
                    }

                    this.invoicingService.generateExistingSalePreview(res);

                    // if editing draft
                    const readOnly = res.status !== ISaleStatus.DRAFT;

                    res.saleLines?.forEach((item) => {
                        const type = item.serviceId ? ItemType.SERVICE : ItemType.PRODUCT;
                        const isBookingAppointment = !!item.bookingId;
                        this.invoicingService.addItemToCart(item, type, isBookingAppointment, readOnly);
                    });
                },
                error: () => {
                    // If error (sale not found), redirect to sales page
                    this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/sales');
                },
            }),
        );
    }

    fetchProducts() {
        this.loading.set(true);

        return this.storesService.getStoreProducts(this.currentStore().id).pipe(
            tap((res) => {
                this.availableProducts.set(res);
                this.filteredProducts.set(res);
                this.loading.set(false);
            }),
        );
    }

    selectPatient(patient: any) {
        this.invoicingService.setPatient(patient);
        this.closeAddPatient();
    }

    clearPatient() {
        this.invoicingService.clearPatient();
    }

    openAddPatient() {
        this.addPatientOpen.set(true);
    }

    closeAddPatient() {
        this.addPatientOpen.set(false);
    }

    addItemToCart(item: any, type: ItemType = ItemType.SERVICE, readonly = false) {
        this.invoicingService.addItemToCart(item, type, false, readonly);
    }

    removeItemFromCart(itemId: string) {
        this.invoicingService.removeItemFromCart(itemId);
    }

    continue() {
        if (!this.canContinue()) {
            return;
        }

        if (this.checkoutMode() === CheckoutMode.EDIT_SALE || this.checkoutMode() === CheckoutMode.INVOICE_REMAINING) {
            const saleId = this.activatedRoute.snapshot?.params['saleId'];
            let saleLines: ICartItem[] = this.invoicingCart().saleLines.filter((line) => !line.readonly);

            saleLines = this.invoicingService.mapSaleLineToSave(saleLines);

            if (saleLines.length === 0 && this.checkoutMode() === CheckoutMode.INVOICE_REMAINING) {
                this.invoiceExistingSale();
                return;
            }
            this.invoicingService.addLinesToSale(saleId, saleLines).subscribe((res) => {
                if (this.checkoutMode() === CheckoutMode.EDIT_SALE) {
                    this.payExistingSale();
                } else if (this.checkoutMode() === CheckoutMode.INVOICE_REMAINING) {
                    this.invoiceExistingSale();
                }
            });
        } else {
            this.payNewSale();
        }
    }

    payNewSale() {
        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/payment-method');
    }

    payExistingSale() {
        const saleId = this.activatedRoute.snapshot?.params['saleId'];
        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/pay/' + saleId);
    }

    invoiceExistingSale() {
        const saleId = this.activatedRoute.snapshot?.params['saleId'];
        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/add-invoice/' + saleId);
    }
}
